import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceArea,
  CartesianGrid,
} from "recharts";
import { useBrand } from "../../context/BrandContext";
import {
  getHrChartYDomain,
  getZoneChartBands,
  HR_CURVE_STROKE,
} from "./zoneUtils";
import styles from "./HeartRateChart.module.css";

/** Downsample like iOS ExerciseDetailsDialogVC.downsampleReadings (peak preserved). */
function downsamplePoints(points, targetCount = 30) {
  if (!points?.length) return [];
  if (points.length <= targetCount) return points;

  let peakIdx = 0;
  for (let i = 1; i < points.length; i++) {
    if ((points[i].bpm || 0) > (points[peakIdx].bpm || 0)) peakIdx = i;
  }

  const step = (points.length - 1) / (targetCount - 1);
  const picked = new Set();
  const result = [];
  for (let i = 0; i < targetCount; i++) {
    const idx = Math.max(0, Math.min(points.length - 1, Math.round(i * step)));
    picked.add(idx);
    result.push(points[idx]);
  }

  if (!picked.has(peakIdx)) {
    const insertAt = Math.min(
      result.length - 1,
      Math.round((peakIdx / (points.length - 1)) * (targetCount - 1))
    );
    result[insertAt] = points[peakIdx];
  }
  return result;
}

function chartTheme(isDark) {
  return {
    axis: isDark ? "rgba(255,255,255,0.85)" : "#0f172a",
    grid: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.10)",
    peakRing: isDark ? "#FFFFFF" : "#FFFFFF",
    tooltipBg: isDark ? "rgba(13, 17, 23, 0.96)" : "rgba(255, 255, 255, 0.96)",
    tooltipBorder: isDark ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.12)",
    tooltipText: isDark ? "#F8FAFC" : "#0F172A",
  };
}

export function HeartRateChart({ hrPoints, mhr, timeLabels }) {
  const { themeMode } = useBrand();
  const isDark = themeMode === "dark";
  const theme = chartTheme(isDark);
  const [yMin, yMax] = getHrChartYDomain(mhr);
  const zoneBands = getZoneChartBands(mhr, isDark);

  const downsampled = downsamplePoints(hrPoints, 30);
  const maxBpmValue =
    downsampled.length > 0 ? Math.max(...downsampled.map((p) => p.bpm)) : 0;
  const peakIndex = downsampled.findIndex((p) => p.bpm === maxBpmValue);

  const labelCount = Math.max(timeLabels?.length || 1, 1);
  const data = downsampled.map((p, i) => {
    const labelIdx =
      downsampled.length <= 1
        ? 0
        : Math.round((i * (labelCount - 1)) / (downsampled.length - 1));
    return {
      idx: i,
      time: timeLabels?.[Math.max(0, Math.min(labelCount - 1, labelIdx))] ?? "",
      bpm: p.bpm,
      isPeak: i === peakIndex && p.bpm > 0,
    };
  });

  const yTicks = zoneBands
    .flatMap((z) => [z.y1, z.y2])
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .sort((a, b) => a - b);

  const hasTimeLabels = Boolean(timeLabels && timeLabels.length > 0);
  const timeTicks =
    hasTimeLabels && data.length > 2
      ? [
          data[0]?.time,
          data[Math.floor(data.length / 2)]?.time,
          data[data.length - 1]?.time,
        ].filter(Boolean)
      : undefined;

  const renderCustomDot = (props) => {
    const { cx, cy, payload } = props;
    if (!payload?.isPeak || cx == null || cy == null) return null;
    return (
      <g key="peak-g">
        {/* Concentric outer halo pulse */}
        <circle cx={cx} cy={cy} r={11} fill="rgba(255, 51, 75, 0.25)">
          <animate
            attributeName="r"
            values="9;14;9"
            dur="2s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.6;0.2;0.6"
            dur="2s"
            repeatCount="indefinite"
          />
        </circle>
        {/* Glow core */}
        <circle
          cx={cx}
          cy={cy}
          r={7.5}
          fill="rgba(255, 51, 75, 0.85)"
          filter="url(#hrCurveGlow)"
        />
        {/* Center dot with white stroke ring matching iOS pulseRing */}
        <circle
          cx={cx}
          cy={cy}
          r={4.5}
          fill={HR_CURVE_STROKE}
          stroke={theme.peakRing}
          strokeWidth={2}
        />
      </g>
    );
  };

  return (
    <div
      className={`${styles.chartShell}${isDark ? ` ${styles.chartShellDark}` : ""}`}
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{
            top: 14,
            right: 18,
            left: -4,
            bottom: hasTimeLabels ? 10 : 6,
          }}
        >
          <defs>
            <filter id="hrCurveGlow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor="#FF334B" floodOpacity="0.9" />
              <feDropShadow dx="0" dy="0" stdDeviation="5" floodColor="#FF334B" floodOpacity="0.55" />
              <feDropShadow dx="0" dy="0" stdDeviation="10" floodColor="#FF334B" floodOpacity="0.25" />
            </filter>
            <linearGradient id="colorHrTraining" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={HR_CURVE_STROKE} stopOpacity={isDark ? 0.40 : 0.32} />
              <stop offset="60%" stopColor={HR_CURVE_STROKE} stopOpacity={isDark ? 0.12 : 0.08} />
              <stop offset="100%" stopColor={HR_CURVE_STROKE} stopOpacity={0} />
            </linearGradient>
          </defs>

          {zoneBands.map(({ zone, color, fillOpacity, borderColor, y1, y2 }) => (
            <ReferenceArea
              key={`zone-${zone}`}
              y1={y1}
              y2={y2}
              fill={color}
              fillOpacity={fillOpacity}
              stroke={borderColor}
              strokeWidth={1}
              ifOverflow="hidden"
            />
          ))}

          <CartesianGrid
            stroke={theme.grid}
            vertical={false}
            strokeDasharray="4 4"
          />
          {hasTimeLabels ? (
            <XAxis
              dataKey="time"
              axisLine={false}
              tickLine={false}
              tick={{ fill: theme.axis, fontSize: 10, fontWeight: 600 }}
              ticks={timeTicks}
              interval="preserveStartEnd"
            />
          ) : (
            <XAxis dataKey="time" hide />
          )}
          <YAxis
            domain={[yMin, yMax]}
            ticks={yTicks}
            width={38}
            tick={{ fill: theme.axis, fontSize: 10, fontWeight: 700 }}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
          />
          <Tooltip
            formatter={(v) => [`${Math.round(v)} BPM`, "Heart Rate"]}
            contentStyle={{
              backgroundColor: theme.tooltipBg,
              borderRadius: 12,
              border: `1px solid ${theme.tooltipBorder}`,
              fontFamily: "inherit",
              color: theme.tooltipText,
              backdropFilter: "blur(12px)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            }}
            labelStyle={{ color: theme.tooltipText }}
            itemStyle={{ color: theme.tooltipText }}
          />
          <Area
            type="monotone"
            dataKey="bpm"
            stroke={HR_CURVE_STROKE}
            strokeWidth={3.2}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="url(#colorHrTraining)"
            dot={renderCustomDot}
            isAnimationActive={false}
            filter="url(#hrCurveGlow)"
            style={{
              filter:
                "drop-shadow(0 0 3px rgba(255, 51, 75, 0.9)) drop-shadow(0 0 8px rgba(255, 51, 75, 0.5))",
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function HeartRateChartBox(props) {
  return <HeartRateChart {...props} />;
}

export default HeartRateChart;
